export function readMeta(name: string, attribute?: string): string | null {
  const tag = document.querySelector(`meta[name=${name}]`) as HTMLElement;
  if (tag == null) {
    return null;
  }

  if (attribute) {
    return tag.getAttribute(attribute);
  }
  return tag.getAttribute('content');
}

export function readMetaBoolean(name: string, attribute?: string): boolean {
  const stringValue = readMeta(name, attribute);
  if (stringValue && stringValue.toLowerCase() === 'true') {
    return true;
  }
  return false;
}

export function readDataNode(): DOMStringMap {
  const appNode = document.getElementById('app');

  if (appNode == null) {
    throw new Error('App node not found. Aborting.');
  }

  return appNode.dataset;
}

/**
 * Reads the SMS modal threshold date from meta tag and validates it.
 * The date should be in ISO 8601 format (e.g., '2026-01-25T00:00:00Z' or '2026-01-25').
 * Returns null if the date is invalid or missing.
 * Logs console errors for invalid dates.
 */
export function getSMSModalThresholdDate(): Date | null {
  const thresholdDateString = readMeta('sms_modal_threshold_date');

  if (!thresholdDateString) {
    console.error(
      '[SMS Modal] Threshold date meta tag is missing. Please set SC_SMS_MODAL_THRESHOLD_DATE environment variable in ISO 8601 format (e.g., "2026-01-25T00:00:00Z").'
    );
    return null;
  }

  const thresholdDate = new Date(thresholdDateString);

  if (Number.isNaN(thresholdDate.getTime())) {
    console.error(
      `[SMS Modal] Invalid threshold date format: "${thresholdDateString}". Expected ISO 8601 format (e.g., "2026-01-25T00:00:00Z" or "2026-01-25").`
    );
    return null;
  }

  return thresholdDate;
}

/**
 * Determines if the SMS modal should be shown based on account creation date.
 * The threshold date is read from the SC_SMS_MODAL_THRESHOLD_DATE environment variable
 * via meta tag. The date should be in ISO 8601 format (e.g., '2026-01-25T00:00:00Z' or '2026-01-25').
 * Returns false if the account was created before the threshold date or if the date is invalid.
 *
 * @param createdAt - The account creation date as an ISO 8601 string (e.g., '2026-01-26T00:00:00Z')
 * @returns true if the account was created on or after the threshold date, false otherwise
 */
export function shouldShowSMSModal(createdAt: string | undefined | null): boolean {
  if (!createdAt) {
    return false;
  }

  // Use readMetaBoolean directly to avoid circular dependency with braze.ts
  const loggingEnabled = readMetaBoolean('braze_enable_logging');

  const thresholdDate = getSMSModalThresholdDate();
  if (!thresholdDate) {
    // Error already logged in getSMSModalThresholdDate
    return false;
  }

  const accountCreatedDate = new Date(createdAt);
  if (Number.isNaN(accountCreatedDate.getTime())) {
    if (loggingEnabled) {
      console.error(`[SMS Modal] Invalid account createdAt date: ${createdAt}`);
    }
    return false;
  }

  if (loggingEnabled) {
    // eslint-disable-next-line no-console
    console.log(
      `[SMS Modal] Comparing dates - Account created: ${accountCreatedDate.toISOString()}, Threshold: ${thresholdDate.toISOString()}, Should show: ${accountCreatedDate >= thresholdDate}`
    );
  }

  return accountCreatedDate >= thresholdDate;
}

export default {
  readMeta,
};
